import React from 'react'
import { CognitoUser } from 'amazon-cognito-identity-js'
import { Auth } from 'aws-amplify'

export enum UserGroup {
  A = 'ITS-A',
  A1 = 'ITS-A1',
  Admin = 'ITS-Admin',
  BrandProtection = 'ITS-Brand-protection',
  BrandProtectionExtended = 'ITS-Brand-protection-extended',
  Distributor = 'ITS-Distributor',
  Franchise = 'ITS-Franchise',
  SuperAdmin = 'ITS-Super-admin',
  Warehouse = 'ITS-Warehouse',
  WarehouseExtended = 'ITS-Warehouse-extended',
  Retail = 'ITS-Retail',
}

const SCAN_PAGE_USER_GROUPS = [
  UserGroup.A,
  UserGroup.A1,
  UserGroup.Admin,
  UserGroup.SuperAdmin,
  UserGroup.BrandProtection,
  UserGroup.Distributor,
  UserGroup.Warehouse,
  UserGroup.WarehouseExtended,
  UserGroup.Franchise,
]

const TRACE_PAGE_USER_GROUPS = [
  UserGroup.Admin,
  UserGroup.SuperAdmin,
  UserGroup.BrandProtection,
  UserGroup.BrandProtectionExtended,
  UserGroup.Retail,
  UserGroup.WarehouseExtended,
]

const BATCH_NUMBERS_USER_GROUPS = [UserGroup.Admin, UserGroup.SuperAdmin]

const AUTHENTIC_CHECK_USER_GROUPS = [
  UserGroup.Admin,
  UserGroup.SuperAdmin,
  UserGroup.BrandProtection,
  UserGroup.BrandProtectionExtended,
  UserGroup.Retail,
]

export interface UserAttributes {
  companyName?: string
  locations?: string[]
  roles?: string[]
}

export type HasGroup = (groups: string[]) => boolean
export type GetUserGroups = () => string[]
export type GetRefreshedAuthData = () => Promise<any>
export type RefreshAuthData = () => void
export type GetGroupDefaultRoute = () => string

export interface AuthContextInterface {
  userAttributes: UserAttributes
  userData: CognitoUser
  hasGroup: HasGroup
  getUserGroups: GetUserGroups
  getGroupDefaultRoute: GetGroupDefaultRoute
  refreshAuthData?: RefreshAuthData
  getRefreshedAuthData?: GetRefreshedAuthData
}

export class AuthData implements AuthContextInterface {
  userData = {} as CognitoUser
  userAttributes = {} as UserAttributes

  getRefreshedAuthData: GetRefreshedAuthData = () => Auth.currentAuthenticatedUser()

  setAuthData = (authData: CognitoUser) => {
    this.userData = authData
  }

  hasGroup: HasGroup = groups => {
    if (!this.userData) return false

    const userData: CognitoUser = this.userData

    if (!userData.getSignInUserSession()) return false

    const cognitoGroups = userData.getSignInUserSession()!.getIdToken().payload['cognito:groups']

    return !!cognitoGroups && !!cognitoGroups.filter((group: string) => groups.includes(group)).length
  }

  getUserGroups: GetUserGroups = () => {
    if (!this.userData) return []

    const authData: CognitoUser = this.userData

    if (!authData.getSignInUserSession()) return []

    return authData.getSignInUserSession()!.getIdToken().payload['cognito:groups']
  }

  getGroupDefaultRoute: GetGroupDefaultRoute = () => {
    if (this.hasGroup(SCAN_PAGE_USER_GROUPS)) return '/scan'
    if (this.hasGroup(TRACE_PAGE_USER_GROUPS)) return '/trace'
    if (this.hasGroup(BATCH_NUMBERS_USER_GROUPS)) return '/batch-numbers'
    if (this.hasGroup(AUTHENTIC_CHECK_USER_GROUPS)) return '/authentic-check'

    return '/no-permissions'
  }
}

export const AuthContext = React.createContext<AuthContextInterface>(new AuthData())
